/**
 * contracts-api 전용 axios 인스턴스 (SPEC: 전자계약 v1→v2 전환, 첫 화면)
 *
 * 왜 별도 인스턴스인가:
 * - v1(src/axios.js)은 inapi(VUE_APP_SERVERURL)를 호출한다. v2 화면은 신규 contracts-api를 호출해야 하며,
 *   두 백엔드가 서로 다른 baseURL을 쓰므로 인스턴스를 분리했다.
 * - v1 인스턴스/인터셉터는 절대 건드리지 않는다(운영 중인 v1 화면 동작 보존이 이번 작업의 절대 전제).
 *
 * 인증:
 * - v1과 "동일한" JWT를 그대로 재사용한다. contracts-api와 inapi가 JWT_ACCESS_SECRET을 공유하므로
 *   accessTokenManu 하나로 양쪽 다 호출 가능하다. 로그인/토큰 발급 플로우는 전부 v1(inapi) 쪽에 있고
 *   여기서는 재사용만 한다.
 *
 * 401 재발급(refresh) 인터셉터를 일부러 복사하지 않은 이유 (판단 근거, 리포트 참고):
 * - src/axios.js의 401 인터셉터는 module-scope 변수(isTokenRefreshing/failedRequestsQueue)로
 *   "동시에 refresh 요청이 여러 번 나가지 않도록" 잠그고 있다. 이 상태는 axios.js 내부에만 있고
 *   export되지 않으므로, v1을 건드리지 않는 한 이 인스턴스에서 그 잠금을 공유할 방법이 없다.
 * - 만약 이 파일에서 별도의 isTokenRefreshing 플래그를 새로 만들면, v1 요청과 v2 요청이 "거의 동시에"
 *   401을 받았을 때 서로 모른 채 각자 /auth/refresh를 호출하게 된다. refresh token은 보통 1회성/회전형이라
 *   두 번째 호출이 첫 번째가 막 갱신한 토큰을 무효화시킬 수 있다 — 이는 v1 로그인 세션 전체에 영향을 주는
 *   회귀이며, "v1 동작 절대 불변" 원칙에 위배된다.
 * - 그래서 이 인스턴스는 401을 자동으로 재시도하지 않는다. 401이 오면 그대로 실패시키고 호출부(화면)의
 *   기존 fail 콜백이 에러를 처리한다. accessToken은 이미 v1 화면들의 정상적인 사용 흐름에서 계속
 *   갱신되고 있으므로, v2 화면 진입 시점에 토큰이 만료돼 있을 가능성은 낮다. 만약 실제로 401이 자주
 *   관측되면, 그때는 axios.js에 refresh 상태를 export하도록 별도 SPEC으로 안전하게 확장해야 한다
 *   (이 작업 범위 밖).
 */
import { getAccessToken } from '@/utils/tokenStore';
import axios from 'axios';
import { withClientHeaders } from '@/utils/clientHeaders';

const contractsApiInstance = axios.create({
  // 운영값만 확정. 스테이징 contracts-api는 VNet 내부에서만 열려 있어 로컬 개발에서 직접 도달 불가 —
  // 팀에서 사용 중인 리버스 프록시/터널이 확정되면 .env.stage에 채워 넣을 것 (README 참고).
  baseURL: process.env.VUE_APP_CONTRACTS_API_URL,
});

contractsApiInstance.interceptors.request.use(
  (config) => {
    withClientHeaders(config); // 화면 위치 헤더(표시용) — contracts-api CORS 는 허용 헤더를 목록으로 막지 않음
    // v1과 동일한 로컬스토리지 키 재사용 — 로그인은 v1 화면에서만 이루어지므로 여기서는 읽기만 한다.
    if (getAccessToken()) {
      config.headers.Authorization = `Bearer ${getAccessToken()}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

contractsApiInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    // 401 자동 재발급 없음 — 위 헤더 주석 참고. 호출부 fail 콜백이 에러를 처리한다.
    console.log(error);
    return Promise.reject(error);
  }
);

export default contractsApiInstance;
